import { FunctionComponent, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    MenuItem,
    Stack,
    SvgIcon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DownloadIcon from '@heroicons/react/24/solid/ArrowDownTrayIcon';
import {
    getInsuranceExcel,
    InsuranceRow,
    ProductEnvironment,
    useGetInsuranceReportQuery,
} from '@/api/v2/insuranceReports';
import {
    downloadBlob,
    formatCount,
    formatEuro,
    formatPct,
} from '@/pageContents/reportsContent/format';

const CHANNEL_LABELS: Record<ProductEnvironment, string> = {
    PORTAL: 'Portal',
    INTEGRATION: 'Webshop',
    B2B: 'B2B',
    HOSPITALITY: 'Hospitality',
};

const ALL_CHANNELS: ProductEnvironment[] = [
    'PORTAL',
    'INTEGRATION',
    'B2B',
    'HOSPITALITY',
];

/**
 * ISO date of the LOCAL calendar day.
 *
 * Not toISOString(): that serialises UTC, and east of Greenwich local midnight is the
 * previous day there — in Vienna the whole settlement period would silently shift back by
 * one, invoicing 31 July twice and 31 August never.
 */
const isoDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate()
    ).padStart(2, '0')}`;

/**
 * The backend bound is exclusive, the picker means the day itself. Null for an incomplete
 * input — a half-typed or cleared date must not reach Date arithmetic, because
 * `new Date(NaN).toISOString()` throws out of the render function and there is no error
 * boundary above this page.
 */
const exclusiveEnd = (isoDay: string): string | null => {
    const [year, month, day] = isoDay.split('-').map(Number);
    if (!year || !month || !day) return null;
    // Local components, so the Date constructor rolls month and year itself and the day
    // count stays right across both daylight-saving switches.
    return isoDate(new Date(year, month - 1, day + 1));
};

/** First and last day of the month before the current one — the period usually settled. */
const defaultPeriod = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: isoDate(from), to: isoDate(to) };
};

export const InsuranceContent: FunctionComponent = () => {
    const initial = useMemo(defaultPeriod, []);
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [channel, setChannel] = useState<ProductEnvironment | 'ALL'>('ALL');
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(false);

    const exclusiveTo = exclusiveEnd(to);
    const rangeIsValid = Boolean(from && exclusiveTo) && from <= to;

    const query = useMemo(
        () => ({
            from,
            to: exclusiveTo ?? from,
            environments: channel === 'ALL' ? undefined : [channel],
        }),
        [from, exclusiveTo, channel]
    );

    // currentData, not data: data keeps the last successful result across argument
    // changes, which would show last month's figures under this month's heading.
    const { currentData, isFetching, isError } = useGetInsuranceReportQuery(
        query,
        {
            skip: !rangeIsValid,
            // The Excel export always fetches fresh. Serving the table from cache would let the
            // two disagree on the invoice figure for the same selection — and the number on
            // screen is the one somebody reads off.
            refetchOnMountOrArgChange: true,
        }
    );

    const download = async () => {
        setIsDownloading(true);
        setDownloadError(false);
        const blob = await getInsuranceExcel(query);
        if (blob) {
            downloadBlob(blob, `versicherungen-${from}-bis-${to}.xlsx`);
        } else {
            setDownloadError(true);
        }
        setIsDownloading(false);
    };

    return (
        <Stack spacing={3}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ md: 'center' }}
            >
                <TextField
                    label='Von'
                    type='date'
                    size='small'
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label='Bis'
                    type='date'
                    size='small'
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label='Vertriebskanal'
                    select
                    size='small'
                    value={channel}
                    onChange={(e) =>
                        setChannel(e.target.value as ProductEnvironment | 'ALL')
                    }
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value='ALL'>Alle Kanäle</MenuItem>
                    {ALL_CHANNELS.map((c) => (
                        <MenuItem key={c} value={c}>
                            {CHANNEL_LABELS[c]}
                        </MenuItem>
                    ))}
                </TextField>

                <Button
                    variant='outlined'
                    disabled={isDownloading || !rangeIsValid}
                    onClick={download}
                    startIcon={
                        <SvgIcon fontSize='small'>
                            <DownloadIcon />
                        </SvgIcon>
                    }
                >
                    {isDownloading ? 'Wird erstellt…' : 'Excel'}
                </Button>

                {isFetching && <CircularProgress size={20} />}
            </Stack>

            {!rangeIsValid && (
                <Alert severity='warning'>
                    Bitte einen vollständigen Zeitraum wählen, bei dem „Von"
                    nicht nach „Bis" liegt.
                </Alert>
            )}

            {isError && (
                <Alert severity='error'>
                    Die Auswertung konnte nicht geladen werden.
                </Alert>
            )}

            {downloadError && (
                <Alert severity='error' onClose={() => setDownloadError(false)}>
                    Der Excel-Export ist fehlgeschlagen.
                </Alert>
            )}

            {currentData && (
                <>
                    <Totals report={currentData} />
                    <SalesTable rows={currentData.rows} />
                    <TerminationsTable
                        rows={currentData.terminationsInPeriod}
                    />
                    <WithoutSalesTable
                        rows={currentData.operatorsWithoutInsurance}
                    />
                </>
            )}
        </Stack>
    );
};

const Totals: FunctionComponent<{
    report: NonNullable<
        ReturnType<typeof useGetInsuranceReportQuery>['currentData']
    >;
}> = ({ report }) => (
    <Card>
        <CardContent>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={4}
                flexWrap='wrap'
            >
                <Figure
                    label='Prämie policiert'
                    hint='Vom Versicherer bestätigt — die Rechnungsbasis. Ändert sich nicht mehr, wenn später storniert wird.'
                    value={formatEuro(report.totals.confirmedPremium)}
                />
                <Figure
                    label='Prämie brutto'
                    hint='Alles, was im Zeitraum verkauft wurde, inkl. unbestätigter Policen.'
                    value={formatEuro(report.totals.grossPremium)}
                />
                <Figure
                    label='Unbestätigt'
                    hint='Bezahlt, aber vom Versicherer nie bestätigt. Klärfall — nicht fakturieren.'
                    value={formatEuro(report.totals.unconfirmedPremium)}
                />
                <Figure
                    label='Offen'
                    hint='Verkauft und noch nicht policiert. Teil der Statusaufteilung.'
                    value={formatEuro(report.totals.openPremium)}
                />
                <Figure
                    label='Heute noch aktiv'
                    hint='Policiert und seither weder storniert, widerrufen noch gesperrt. Kontrollzahl.'
                    value={formatEuro(report.totals.activePremium)}
                />
                <Figure
                    label='Policen'
                    value={formatCount(report.totals.soldCount)}
                />
            </Stack>
        </CardContent>
    </Card>
);

const Figure: FunctionComponent<{
    label: string;
    value: string;
    hint?: string;
}> = ({ label, value, hint }) => (
    <Stack spacing={0.5}>
        <Tooltip title={hint ?? ''} placement='top'>
            <Typography variant='body2' color='text.secondary'>
                {label}
            </Typography>
        </Tooltip>
        <Typography variant='h6'>{value}</Typography>
    </Stack>
);

const SalesTable: FunctionComponent<{ rows: InsuranceRow[] }> = ({ rows }) => {
    if (!rows.length) {
        return (
            <Alert severity='info'>
                Im gewählten Zeitraum wurde keine Versicherung verkauft.
            </Alert>
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant='h6' sx={{ mb: 2 }}>
                    Verkäufe je Bergbahn und Tarif
                </Typography>
                <TableContainer>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Bergbahn</TableCell>
                                <TableCell>Tarif</TableCell>
                                <TableCell align='right'>Verkauft</TableCell>
                                <TableCell align='right'>
                                    Prämie policiert
                                </TableCell>
                                <TableCell align='right'>Unbestätigt</TableCell>
                                <TableCell align='right'>Aktiv</TableCell>
                                <TableCell align='right'>Offen</TableCell>
                                <TableCell align='right'>Storno</TableCell>
                                <TableCell align='right'>Widerruf</TableCell>
                                <TableCell align='right'>Gesperrt</TableCell>
                                <TableCell align='right'>
                                    Anteil an Tickets
                                </TableCell>
                                <TableCell>Kanäle</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow
                                    key={`${r.liftOperatorId}-${r.tariffIdentifier}`}
                                >
                                    <TableCell>
                                        {r.liftOperatorName ?? r.liftOperatorId}
                                    </TableCell>
                                    <TableCell>
                                        {r.tariffName ?? r.tariffIdentifier}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatCount(r.soldCount)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatEuro(r.confirmedPremium)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {r.unconfirmedCount > 0
                                            ? `${formatCount(r.unconfirmedCount)} · ${formatEuro(r.unconfirmedPremium)}`
                                            : '—'}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatCount(r.activeCount)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatCount(r.openCount)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatCount(r.canceledCount)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatCount(r.revokedCount)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatCount(r.permissionBlockedCount)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatPct(r.attachRatePct)}
                                    </TableCell>
                                    <TableCell>
                                        <Stack
                                            direction='row'
                                            spacing={0.5}
                                            flexWrap='wrap'
                                            useFlexGap
                                        >
                                            {r.channels.map((c) => (
                                                <Chip
                                                    key={`${c.environment}-${c.externalPayment}`}
                                                    size='small'
                                                    variant='outlined'
                                                    label={`${CHANNEL_LABELS[c.environment]}${
                                                        c.externalPayment
                                                            ? ' (Bahn)'
                                                            : ''
                                                    }: ${c.soldCount}`}
                                                />
                                            ))}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ mt: 1, display: 'block' }}
                >
                    „Anteil an Tickets" bezieht sich auf alle verkauften Tickets
                    der Bahn, nicht nur auf die versicherbaren — die Werte
                    mehrerer Tarife teilen sich denselben Nenner und dürfen
                    nicht addiert werden. Aktiv, Offen, Storno, Widerruf und
                    Gesperrt ergeben zusammen die Bruttoprämie; „Unbestätigt"
                    ist eine überlappende Zweitsicht und gehört nicht in diese
                    Summe.
                </Typography>
            </CardContent>
        </Card>
    );
};

const TerminationsTable: FunctionComponent<{
    rows: InsuranceReportTerminations;
}> = ({ rows }) => {
    if (!rows.length) return null;

    return (
        <Card>
            <CardContent>
                <Typography variant='h6' sx={{ mb: 1 }}>
                    Stornos und Widerrufe im Zeitraum
                </Typography>
                <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 2 }}
                >
                    Was in diesem Zeitraum zurückgenommen wurde — unabhängig
                    davon, wann es verkauft wurde. Diese Tabelle ist die
                    Gutschriftseite und stimmt bewusst nicht mit den
                    Storno-Spalten oben überein.
                </Typography>
                <TableContainer>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Bergbahn</TableCell>
                                <TableCell>Tarif</TableCell>
                                <TableCell align='right'>Stornos</TableCell>
                                <TableCell align='right'>
                                    Prämie storniert
                                </TableCell>
                                <TableCell align='right'>Widerrufe</TableCell>
                                <TableCell align='right'>
                                    Prämie widerrufen
                                </TableCell>
                                <TableCell align='right'>
                                    Davon Kundengutschrift
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow
                                    key={`${r.liftOperatorId}-${r.tariffIdentifier}`}
                                >
                                    <TableCell>
                                        {r.liftOperatorName ?? r.liftOperatorId}
                                    </TableCell>
                                    <TableCell>
                                        {r.tariffName ?? r.tariffIdentifier}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatCount(r.canceledCount)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatEuro(r.canceledPremium)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatCount(r.revokedCount)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatEuro(r.revokedPremium)}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatEuro(r.refundedAmount)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ mt: 1, display: 'block' }}
                >
                    Nicht jede Stornierung erstattet Geld — ein Embargo setzt
                    das Storno ohne Kundengutschrift. Deshalb steht die
                    tatsächliche Gutschrift als eigene Spalte daneben.
                </Typography>
            </CardContent>
        </Card>
    );
};

const WithoutSalesTable: FunctionComponent<{
    rows: InsuranceReportWithoutSales;
}> = ({ rows }) => {
    if (!rows.length) return null;

    return (
        <Card>
            <CardContent>
                <Typography variant='h6' sx={{ mb: 2 }}>
                    Bergbahnen ohne Versicherungsverkauf
                </Typography>
                <TableContainer>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Bergbahn</TableCell>
                                <TableCell align='right'>
                                    Tickets im Zeitraum
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.liftOperatorId}>
                                    <TableCell>
                                        {r.liftOperatorName ?? r.liftOperatorId}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {formatCount(r.ticketsSold)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

type InsuranceReportTerminations = NonNullable<
    ReturnType<typeof useGetInsuranceReportQuery>['currentData']
>['terminationsInPeriod'];

type InsuranceReportWithoutSales = NonNullable<
    ReturnType<typeof useGetInsuranceReportQuery>['currentData']
>['operatorsWithoutInsurance'];
